import { useEffect, useState } from "react";
import { LanguagesFetchAPI } from "../../../api/Languages";
import { TranslationsFetchAPI } from "../../../api/Translations";

const asArray = (value) => Array.isArray(value) ? value : [];
const getApiError = (err, fallback = "No se pudo completar la acción.") => (
  err?.response?.data?.error || err?.response?.data?.message || fallback
);
const getLangCode = (lang) => lang?.code || lang?.language_code || lang?.iso_code || "";
const getLangLabel = (lang) => lang?.name || lang?.language_name || getLangCode(lang);

/**
 * Small "🌐 Idiomas" button that opens a panel to view, save and remove
 * translations (name/description) of a single row (producto, categoría,
 * segmento o item) for any language registered in `languages`.
 *
 * entity: 'product' | 'category' | 'segment' | 'item'
 * entityId: the row id on the base table (group_id, category_id, segment_id, item_id)
 */
export function TranslationsButton({ entity, entityId, title = "", compact = false, className = "" }) {
  const [open, setOpen] = useState(false);

  if (!entity || !entityId) return null;

  return (
    <>
      <button
        type="button"
        className={`pp-btn-ghost pp-btn-ghost--sm pp-translations-btn ${className}`}
        title="Traducciones / Idiomas"
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
      >
        <span aria-hidden="true">🌐</span>{!compact && <span> Idiomas</span>}
      </button>
      {open && (
        <TranslationsModal
          entity={entity}
          entityId={entityId}
          fallbackName={title}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function TranslationsModal({ entity, entityId, fallbackName, onClose }) {
  const token = localStorage.getItem("x-access-token");
  const [languages, setLanguages] = useState([]);
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [languageCode, setLanguageCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingCode, setDeletingCode] = useState("");

  const load = async () => {
    try {
      setLoading(true);
      setError("");
      const [languagesRes, translationsRes] = await Promise.allSettled([
        LanguagesFetchAPI.getLanguages(token),
        TranslationsFetchAPI.getTranslations(entity, entityId, token),
      ]);

      setLanguages(languagesRes.status === "fulfilled" ? asArray(languagesRes.value?.data?.languages) : []);

      if (translationsRes.status === "fulfilled") {
        setTranslations(asArray(translationsRes.value?.data?.data));
      } else {
        setTranslations([]);
        setError(getApiError(translationsRes.reason, "No se pudieron cargar las traducciones."));
      }
    } catch (err) {
      console.log(err);
      setError(getApiError(err, "No se pudieron cargar las traducciones."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity, entityId]);

  const selectLanguage = (code) => {
    setLanguageCode(code);
    const existing = translations.find(t => t.language_code === code);
    setName(existing?.name || "");
    setDescription(existing?.description || "");
  };

  const save = async () => {
    if (!languageCode) return;
    setSaving(true);
    try {
      await TranslationsFetchAPI.upsertTranslation(entity, entityId, {
        language_code: languageCode,
        name: name.trim(),
        description: description.trim(),
      }, token);
      await load();
    } catch (err) {
      console.log(err);
      setError(getApiError(err, "No se pudo guardar la traducción."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (code) => {
    setDeletingCode(code);
    try {
      await TranslationsFetchAPI.deleteTranslation(entity, entityId, code, token);
      if (code === languageCode) {
        setLanguageCode("");
        setName("");
        setDescription("");
      }
      await load();
    } catch (err) {
      console.log(err);
      setError(getApiError(err, "No se pudo eliminar la traducción."));
    } finally {
      setDeletingCode("");
    }
  };

  const availableLanguages = languages.filter(lang => getLangCode(lang));

  return (
    <div className="pp-overlay" onClick={onClose}>
      <div className="pp-modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="pp-modal-pad--head">
          <h3 className="pp-modal-title">🌐 Idiomas{fallbackName ? ` · ${fallbackName}` : ""}</h3>
        </div>
        <div className="pp-modal-pad">
          {loading ? (
            <div className="pp-seg-loading">Cargando idiomas...</div>
          ) : (
            <>
              {error && <div className="pp-translations-error">{error}</div>}

              <div className="pp-form-group">
                <label className="pp-form-label">Idioma</label>
                <select
                  className="pp-form-select"
                  value={languageCode}
                  onChange={e => selectLanguage(e.target.value)}
                  disabled={availableLanguages.length === 0}
                >
                  <option value="">{availableLanguages.length === 0 ? "No hay idiomas configurados" : "Selecciona un idioma"}</option>
                  {availableLanguages.map(lang => (
                    <option key={getLangCode(lang)} value={getLangCode(lang)}>
                      {getLangLabel(lang)} ({getLangCode(lang)})
                    </option>
                  ))}
                </select>
              </div>

              {languageCode && (
                <>
                  <div className="pp-form-group">
                    <label className="pp-form-label">Nombre</label>
                    <input
                      className="pp-form-input"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder={fallbackName || "Nombre traducido"}
                    />
                  </div>
                  <div className="pp-form-group">
                    <label className="pp-form-label">Descripción</label>
                    <textarea
                      className="pp-form-textarea"
                      value={description}
                      onChange={e => setDescription(e.target.value)}
                      placeholder="Descripción traducida"
                    />
                  </div>
                  <div className="pp-flex-end" style={{ marginTop: 4, marginBottom: 4 }}>
                    <button className="pp-btn-primary" disabled={saving} onClick={save}>
                      {saving ? "Guardando..." : "Guardar traducción"}
                    </button>
                  </div>
                </>
              )}

              <div className="pp-section-label" style={{ marginTop: 16 }}>Traducciones guardadas</div>
              {translations.length === 0 ? (
                <div className="pp-seg-loading">Aún no hay traducciones para esta entidad.</div>
              ) : (
                <div className="pp-translations-list">
                  {translations.map(t => (
                    <div key={t.language_code} className="pp-translations-item">
                      <div className="pp-translations-item-info">
                        <span className="pp-lib-card-title">
                          {languages.find(l => getLangCode(l) === t.language_code) ? getLangLabel(languages.find(l => getLangCode(l) === t.language_code)) : t.language_code}
                          <span className="pp-seg-meta" style={{ marginLeft: 6 }}>{t.language_code}</span>
                        </span>
                        {t.name && <div className="pp-lib-card-desc">{t.name}</div>}
                      </div>
                      <div className="pp-translations-item-actions">
                        <button className="pp-btn-ghost pp-btn-ghost--sm" onClick={() => selectLanguage(t.language_code)}>Editar</button>
                        <button
                          className="pp-btn-ghost pp-btn-ghost--sm pp-danger"
                          disabled={deletingCode === t.language_code}
                          onClick={() => remove(t.language_code)}
                        >
                          {deletingCode === t.language_code ? "..." : "Eliminar"}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="pp-modal-footer">
          <button className="pp-btn-secondary" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}
